import React, { useState, useEffect, useRef } from 'react';
import { 
  Mic, Square, Play, LogOut, Settings, Users, Video, 
  Calendar as CalendarIcon, CheckCircle2, XCircle, Plus, Trash2, Edit2, PlayCircle, StopCircle,
  Ban, CheckCircle, Sparkles, Smile, Send, MessageCircle, Upload, ArrowLeftRight,
  Bookmark, ThumbsUp, User as UserIcon, Lock, Unlock, EyeOff, Image as ImageIcon, Mail
} from 'lucide-react';

// === Firebase Cloud Firestore 整合 ===
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail, signInWithCustomToken } from 'firebase/auth';
import { getFirestore, collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, orderBy, query, where, getDocs, arrayUnion, arrayRemove } from 'firebase/firestore';

// 🏷️ 專案版號
const APP_VERSION = "v4.21";

// 🚨 【關鍵動作】：請將下方內容替換為您在 Firebase 控制台取得的「真實金鑰」！
const myGCPConfig = {
  apiKey: "AIzaSyBCnqEYanv9XChQr7sRLKbkWfTLYmS1VyQ",
  authDomain: "yuli-japanese-cloud.firebaseapp.com",
  projectId: "yuli-japanese-cloud",
  storageBucket: "yuli-japanese-cloud.firebasestorage.app",
  messagingSenderId: "767239207925",
  appId: "1:767239207925:web:19742542b7af160a597ebc",
};

// 安全地讀取全域變數
const getGlobalConfig = () => {
  if (typeof window !== 'undefined' && window.__firebase_config) {
    try { return JSON.parse(window.__firebase_config); } catch (e) { return myGCPConfig; }
  }
  return myGCPConfig;
};

const firebaseConfig = getGlobalConfig();
const isCanvasEnv = typeof window !== 'undefined' && !!window.__firebase_config;
const isDummyConfig = !isCanvasEnv && firebaseConfig.apiKey === "YOUR_API_KEY";

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// 🛡️ 雙實例技術：專門給管理員在背景「註冊學生」用，防止管理員被強制登出
const secondaryApp = initializeApp(firebaseConfig, "Secondary");
const secondaryAuth = getAuth(secondaryApp);

// 確保資料庫連線路徑完全正確
const rawAppId = (typeof window !== 'undefined' && window.__app_id) ? window.__app_id : 'mimitube-gcp';
const safeAppId = rawAppId.replace(/[^a-zA-Z0-9_-]/g, '-');
const getPath = (col) => isCanvasEnv ? `artifacts/${safeAppId}/public/data/${col}` : col;

// --- 輔助函式：取得當地時間的 YYYY-MM-DD ---
const getLocalTodayDate = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// --- 初始資料 ---
const INITIAL_USERS = [
  { id: 'u1', email: 'student@yulitube.com', role: 'student', name: '王小明', status: 'active', avatar: '' },
  { id: 'u2', email: 'admin@yulitube.com', role: 'admin', name: '管理員老師', status: 'active', avatar: '' }
];

const INITIAL_SETTINGS = {
  maxRecordingSeconds: 60,
  retentionMonths: 6
};

// --- 主應用程式 ---
export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentView, setCurrentView] = useState('student'); 
  const [fbUser, setFbUser] = useState(null);
  const [isAuthChecking, setIsAuthChecking] = useState(true); 
  const [dbError, setDbError] = useState('');
  
  const [lessons, setLessons] = useState({});
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [recordings, setRecordings] = useState([]);

  const cleanupRef = useRef(false);

  // 1. 初始化授權狀態監聽
  useEffect(() => {
    if (isDummyConfig) return;
    
    const initAuth = async () => {
        if (window.__initial_auth_token) {
            try { await signInWithCustomToken(auth, window.__initial_auth_token); } 
            catch (e) { console.error(e); }
        }
    };
    initAuth();

    const unsub = onAuthStateChanged(auth, async (user) => {
      setFbUser(user);
      
      if (user) {
         // 透過 Auth UID 尋找對應的會員資料
         const q = query(collection(db, getPath('users')), where('authUid', '==', user.uid));
         const snap = await getDocs(q);
         if (!snap.empty) {
            const userData = snap.docs[0].data();
            if (userData.status === 'active') {
               setCurrentUser(userData);
               setCurrentView(userData.role === 'admin' ? 'admin' : 'student');
            } else {
               await signOut(auth);
               setDbError("此帳號已被停權 😢");
            }
         } else {
            // 自動修復：如果用 Email 登入，但資料庫裡還沒綁定 UID，自動綁上去
            const qEmail = query(collection(db, getPath('users')), where('email', '==', user.email));
            const snapEmail = await getDocs(qEmail);
            if (!snapEmail.empty) {
               const userData = snapEmail.docs[0].data();
               if (userData.status === 'active') {
                   if (!isCanvasEnv) {
                       await updateDoc(doc(db, getPath('users'), userData.id), { authUid: user.uid });
                   }
                   setCurrentUser({...userData, authUid: user.uid});
                   setCurrentView(userData.role === 'admin' ? 'admin' : 'student');
               } else {
                   await signOut(auth);
                   setDbError("此帳號已被停權 😢");
               }
            } else {
               await signOut(auth);
               setDbError("找不到對應的會員資料 😢");
            }
         }
      } else {
         setCurrentUser(null);
      }
      setIsAuthChecking(false);
    });
    return () => unsub();
  }, []);

  // 2. 獲取資料 (🔥資安升級：只有管理員才能下載全校名單)
  useEffect(() => {
    if (!currentUser || isDummyConfig) return;

    const qRecordings = query(collection(db, getPath('recordings')), orderBy('timestamp', 'desc'));

    const unsubs = [
      onSnapshot(collection(db, getPath('lessons')), snap => {
        const loaded = {};
        snap.docs.forEach(d => loaded[d.id] = d.data());
        setLessons(loaded);
      }),
      onSnapshot(doc(db, getPath('settings'), 'global'), d => {
        if (d.exists()) setSettings(d.data());
        else if (!isCanvasEnv) setDoc(doc(db, getPath('settings'), 'global'), INITIAL_SETTINGS);
      }),
      onSnapshot(qRecordings, snap => {
        setRecordings(snap.docs.map(d => ({...d.data(), id: d.id})));
      })
    ];

    // 只有管理員才准許監聽整個 users 集合，學生端拒絕下載！
    if (currentUser.role === 'admin') {
       const usersUnsub = onSnapshot(collection(db, getPath('users')), snap => {
          if (snap.empty && !isCanvasEnv) {
             INITIAL_USERS.forEach(u => setDoc(doc(db, getPath('users'), u.id), u));
          } else {
             setUsers(snap.docs.map(d => d.data()));
          }
       }, err => console.error("讀取會員失敗", err));
       unsubs.push(usersUnsub);
    } else {
       setUsers([]); // 學生保持空陣列，防止資料外洩
    }

    return () => unsubs.forEach(u => u());
  }, [currentUser]);

  // 自動清理過期資料
  useEffect(() => {
    if (currentUser?.role === 'admin' && settings && recordings.length > 0 && !cleanupRef.current) {
      cleanupRef.current = true; 
      const performCleanup = async () => {
        const retentionMonths = settings.retentionMonths || 6;
        const now = new Date();
        const cutoffDate = new Date(now.setMonth(now.getMonth() - retentionMonths));
        let deletedCount = 0;
        const promises = recordings.map(async (rec) => {
          if (new Date(rec.timestamp) < cutoffDate) {
            try { await deleteDoc(doc(db, getPath('recordings'), rec.id)); deletedCount++; } catch (err) {}
          }
        });
        await Promise.all(promises);
        if (deletedCount > 0) console.log(`[系統維護] 已刪除 ${deletedCount} 筆過期資料。`);
      };
      performCleanup();
    }
  }, [currentUser, settings, recordings]);

  const handleLogout = async () => {
    setCurrentUser(null);
    setCurrentView('student');
    cleanupRef.current = false;
    try {
        await signOut(auth);
    } catch (error) {
        console.error("登出錯誤", error);
    }
  };

  const toggleView = () => {
    setCurrentView(prev => prev === 'admin' ? 'student' : 'admin');
  };

  if (isDummyConfig) return (
    <div className="min-h-screen bg-[#FFFBF0] flex items-center justify-center p-6 text-center font-sans">
      <div className="bg-white p-10 rounded-[2rem] shadow-xl border-4 border-pink-100 max-w-md">
        <h2 className="text-2xl font-extrabold text-slate-700 mb-4">請填入真實的 Firebase 金鑰</h2>
        <p className="text-slate-500 mb-6">偵測到您尚未替換程式碼中的 <code>myGCPConfig</code> 設定內容。</p>
        <div className="bg-pink-50 text-pink-600 px-4 py-2 rounded-xl text-sm font-bold">Ver {APP_VERSION}</div>
      </div>
    </div>
  );

  if (isAuthChecking) return (
    <div className="min-h-screen bg-[#FFFBF0] flex flex-col items-center justify-center gap-6">
      <div className="relative">
        <div className="absolute inset-0 bg-pink-200 rounded-full blur-xl opacity-50 animate-pulse"></div>
        <div className="bg-white p-6 rounded-full shadow-lg relative z-10 animate-bounce"><Smile className="w-16 h-16 text-pink-400" /></div>
      </div>
      <p className="text-xl font-extrabold text-slate-600 tracking-wider">YuliTube 準備中...</p>
    </div>
  );

  // 傳入 setDbError 以利在重新登入時清除先前的錯誤
  if (!currentUser) return <LoginScreen dbError={dbError} setDbError={setDbError} />;

  return (
    <div className="min-h-screen bg-[#FFFBF0] text-slate-700 font-sans selection:bg-pink-200 pb-10">
      <nav className="bg-white shadow-sm border-b-4 border-pink-100 px-4 py-3 flex justify-between items-center sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <div className="bg-pink-400 p-2 rounded-2xl shadow-sm rotate-3"><Smile className="w-5 h-5 text-white" /></div>
          <h1 className="text-lg sm:text-xl font-extrabold text-slate-700 tracking-wider">YuliTube</h1>
          <span className="bg-pink-100 text-pink-600 text-[10px] font-bold px-2 py-1 rounded-full border border-pink-200 hidden sm:block">{APP_VERSION}</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {currentUser.role === 'admin' && (
            <button onClick={toggleView} className="flex items-center gap-1.5 px-3 py-2 bg-pink-50 text-pink-600 rounded-full font-bold text-xs border-2 border-pink-100 hover:bg-pink-100 transition-colors">
              <ArrowLeftRight className="w-4 h-4" />
              <span className="hidden sm:inline">{currentView === 'admin' ? '前往前台' : '管理後台'}</span>
            </button>
          )}
          
          <div className="flex items-center gap-2 bg-orange-50 px-2 py-1.5 rounded-full border-2 border-orange-100 text-xs font-bold text-orange-800 max-w-[140px] sm:max-w-xs">
            <UserAvatar avatar={currentUser.avatar} name={currentUser.name} className="w-6 h-6 border border-orange-200" />
            <span className="truncate max-w-[60px] sm:max-w-none">{currentUser.name}</span>
            <div className="w-px h-3 bg-orange-200 mx-1 shrink-0"></div>
            <button onClick={handleLogout} className="text-orange-500 hover:text-pink-500 shrink-0"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto p-3 lg:p-8">
        {currentView === 'admin' && currentUser.role === 'admin' ? (
          <AdminDashboard users={users} lessons={lessons} settings={settings} />
        ) : (
          <StudentDashboard lessons={lessons} settings={settings} currentUser={currentUser} recordings={recordings} users={users} />
        )}
      </main>
      
      <div className="fixed bottom-2 left-2 text-[10px] text-slate-300 font-mono select-none">Build: {APP_VERSION}</div>
    </div>
  );
}

// --- 頭像元件 ---
function UserAvatar({ avatar, name, className = "w-8 h-8" }) {
  const [imgError, setImgError] = useState(false);
  
  if (avatar && !imgError) {
    const src = avatar.startsWith('data:') ? avatar : `avatars/${avatar}`;
    return (
      <img 
        src={src} 
        alt={name} 
        className={`${className} rounded-full object-cover bg-white`}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div className={`${className} rounded-full bg-pink-100 flex items-center justify-center text-pink-400 shrink-0`}>
      <UserIcon size={14} fill="currentColor" />
    </div>
  );
}

// --- 日期標記元件 ---
function LessonDateIndicators({ lessons, onSelectDate, selectedDate }) {
  const selectedMonth = selectedDate.slice(0, 7);
  const today = getLocalTodayDate();

  const dates = Object.keys(lessons)
    .filter(date => date.startsWith(selectedMonth))
    .filter(date => date <= today) 
    .sort((a, b) => new Date(b) - new Date(a));

  if (dates.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t-2 border-dashed border-pink-100">
      <p className="text-xs font-bold text-slate-400 mb-2 flex items-center gap-1">
        <Bookmark size={12} className="text-orange-400" /> {selectedMonth.split('-')[1]} 月份課程：
      </p>
      <div className="flex flex-wrap gap-2 w-full">
        {dates.map(date => {
          const [y, m, d] = date.split('-');
          const label = `${Number(m)}/${Number(d)}`;
          return (
            <button
              key={date}
              onClick={() => onSelectDate(date)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all shadow-sm border-2 ${
                selectedDate === date
                  ? 'bg-pink-400 text-white border-pink-400 transform scale-105 shadow-pink-200'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-pink-300 hover:text-pink-500'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// --- 登入畫面 (純淨版，修復非會員卡住 Bug) ---
function LoginScreen({ dbError, setDbError }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // 監聽來自外層 App 的資料庫錯誤。如果發生錯誤，解除 isLoading 狀態。
  useEffect(() => {
    if (dbError) {
      setIsLoading(false);
    }
  }, [dbError]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMsg('');
    if (setDbError) setDbError(''); // 清除上一次的資料庫錯誤

    try {
      const auth = getAuth();
      await signInWithEmailAndPassword(auth, email, password);
      // 成功登入後，App.jsx 的監聽器會自動載入資料，不需在這裡特別處理。
      // 若後續 App.jsx 發現資料庫沒這個人，會透過上面的 useEffect 解除 loading 狀態。
    } catch (err) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
         setError('Email 或密碼錯誤 💦');
      } else {
         setError('登入發生錯誤：' + err.message);
      }
      setIsLoading(false); // 登入失敗時立刻解除 loading
    }
  };

  const handleResetPassword = async () => {
    if (!email || !email.includes('@')) {
      setError('請先在上方欄位輸入您正確的 Email');
      setMsg('');
      return;
    }
    setError('');
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, email);
      setMsg('✅ 密碼重設信已寄出！請前往信箱收信設定新密碼。');
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setError('找不到此 Email 的註冊紀錄。');
      } else {
        setError('發送失敗：' + err.message);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF0] flex items-center justify-center p-4 relative overflow-hidden font-sans">
      <div className="absolute top-10 left-10 w-32 h-32 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
      <div className="absolute top-0 right-20 w-32 h-32 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
      <div className="absolute -bottom-8 left-40 w-32 h-32 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>

      <div className="bg-white p-8 sm:p-10 rounded-[2.5rem] shadow-xl border-4 border-pink-100 w-full max-w-md relative z-10">
        <div className="flex flex-col items-center mb-8">
          <div className="bg-pink-400 p-4 rounded-3xl mb-4 shadow-lg shadow-pink-200 transform -rotate-3"><Smile className="w-10 h-10 text-white" /></div>
          <h1 className="text-3xl font-extrabold text-slate-700">YuliTube</h1>
          <p className="text-pink-500 font-bold text-sm mt-2 bg-pink-50 px-4 py-1 rounded-full border border-pink-100">快樂學日語 ✨ {APP_VERSION}</p>
        </div>
        
        {(error || dbError) && <div className="bg-red-50 text-red-500 font-bold p-3 rounded-xl text-sm mb-4 border-2 border-red-100 flex items-center gap-2"><XCircle className="w-5 h-5 shrink-0" /> <span className="break-words">{error || dbError}</span></div>}
        {msg && <div className="bg-green-50 text-green-600 font-bold p-3 rounded-xl text-sm mb-4 border-2 border-green-100 flex items-center gap-2"><CheckCircle2 className="w-5 h-5 shrink-0" /> <span className="break-words">{msg}</span></div>}
        
        <form onSubmit={handleLogin} className="space-y-4">
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full p-4 bg-slate-50 border-2 border-slate-200 rounded-2xl outline-none focus:border-pink-400 font-bold text-slate-700" placeholder="註冊 Email" required disabled={isLoading} />
          <div className="relative">
             <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full p-4 bg-slate-50 border-2 border-slate-200 rounded-2xl outline-none focus:border-pink-400 font-bold text-slate-700" placeholder="密碼" required disabled={isLoading} />
          </div>
          
          <div className="flex justify-end mt-1 mb-2">
            <button type="button" onClick={handleResetPassword} className="text-sm text-pink-500 font-bold hover:text-pink-600 hover:underline">忘記密碼？</button>
          </div>

          <button type="submit" disabled={isLoading} className="w-full bg-pink-400 hover:bg-pink-500 text-white font-extrabold text-lg py-4 rounded-2xl shadow-lg shadow-pink-200 transition-all hover:-translate-y-1 disabled:opacity-50">
            {isLoading ? '登入驗證中...' : '登入學習'}
          </button>
        </form>
      </div>
    </div>
  );
}

// --- 學生端 ---
function StudentDashboard({ lessons, settings, currentUser, recordings, users }) {
  const [selectedDate, setSelectedDate] = useState(getLocalTodayDate());
  const currentLesson = lessons[selectedDate];
  
  const [activeMedia, setActiveMedia] = useState(null);
  const handleMediaPlay = (type, id) => { setActiveMedia({ type, id }); };

  // 新增：當日期切換時，強制清空所有正在播放的媒體狀態
  useEffect(() => {
    setActiveMedia(null);
  }, [selectedDate]);

  const isFuture = selectedDate > getLocalTodayDate();

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="w-full lg:w-1/3 space-y-6">
        <div className="bg-white p-5 lg:p-6 rounded-[2rem] shadow-sm border-4 border-pink-50">
          <h2 className="text-xl font-extrabold flex items-center gap-2 mb-4 text-pink-500"><div className="bg-pink-100 p-2 rounded-xl"><CalendarIcon className="w-6 h-6" /></div>選擇日期</h2>
          <input 
            type="date" 
            value={selectedDate} 
            onChange={(e) => setSelectedDate(e.target.value)} 
            className="w-full h-14 p-4 bg-slate-50 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-pink-100 focus:border-pink-400 outline-none font-bold text-slate-700 cursor-pointer hover:bg-white transition-colors appearance-none"
            style={{ WebkitAppearance: 'none' }} 
          />
          <LessonDateIndicators lessons={lessons} onSelectDate={setSelectedDate} selectedDate={selectedDate} />
          
          <div className="mt-6">
            <h3 className="font-bold text-slate-500 mb-3 ml-1">當日課程</h3>
            {isFuture ? (
              <div className="p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 text-center font-bold flex flex-col items-center gap-2">
                <EyeOff className="w-10 h-10 text-slate-300" />
                <span className="text-sm">課程準備中，敬請期待！</span>
              </div>
            ) : currentLesson ? (
              <div className="p-5 bg-gradient-to-br from-orange-50 to-pink-50 border-2 border-pink-100 rounded-2xl shadow-sm hover:-translate-y-1 transition-transform">
                <p className="font-extrabold text-lg text-pink-700">{currentLesson.title}</p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="bg-white text-pink-500 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm border border-pink-100">{currentLesson.segments?.length || 0} 個練習片段</span>
                </div>
              </div>
            ) : <div className="p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 text-center font-bold">這天沒有課程喔 💤</div>}
          </div>
        </div>
      </div>
      <div className="w-full lg:w-2/3">
        {isFuture ? (
           <div className="bg-white p-12 rounded-[2rem] shadow-sm border-4 border-pink-50 text-center min-h-[400px] flex flex-col items-center justify-center font-bold text-slate-400">
             <div className="bg-pink-50 p-6 rounded-full mb-6"><Sparkles className="w-16 h-16 text-pink-300" /></div>
             未來的課程還在準備中喔！
           </div>
        ) : currentLesson ? (
          <VideoLearningModule lesson={currentLesson} settings={settings} currentUser={currentUser} recordings={recordings} date={selectedDate} activeMedia={activeMedia} onMediaPlay={handleMediaPlay} users={users} />
        ) : <div className="bg-white p-12 rounded-[2rem] shadow-sm border-4 border-pink-50 text-center min-h-[400px] flex flex-col items-center justify-center font-bold text-slate-400"><div className="bg-pink-50 p-6 rounded-full mb-6"><Video className="w-16 h-16 text-pink-300" /></div>請由左側選擇日期開始學習 ✨</div>}
      </div>
    </div>
  );
}

function VideoLearningModule({ lesson, settings, currentUser, recordings, date, activeMedia, onMediaPlay, users }) {
  const [activeSegment, setActiveSegment] = useState(null);
  const [videoError, setVideoError] = useState('');
  const playerRef = useRef(null);
  const loopIntervalRef = useRef(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);

  // 當「日期」切換時，強制清除循環狀態並暫停影片
  useEffect(() => {
    setActiveSegment(null);
    if (loopIntervalRef.current) clearInterval(loopIntervalRef.current);
    if (playerRef.current && playerRef.current.pauseVideo) {
      playerRef.current.pauseVideo();
    }
  }, [date]);

  useEffect(() => {
    if (!lesson.videoId) return;
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
    const init = () => {
      if (playerRef.current) return;
      playerRef.current = new window.YT.Player('yt-player', {
        videoId: lesson.videoId,
        playerVars: { autoplay: 0, controls: 1, rel: 0 },
        events: {
          onReady: () => setIsPlayerReady(true),
          onError: (e) => {
            if (e.data === 101 || e.data === 150) setVideoError('這部影片不允許嵌入播放 😢');
            else setVideoError('影片無法播放 😢');
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) onMediaPlay('video', 'yt-player');
          }
        }
      });
    };
    if (window.YT && window.YT.Player) init();
    else window.onYouTubeIframeAPIReady = init;
    
    if (playerRef.current && isPlayerReady) {
      playerRef.current.cueVideoById(lesson.videoId);
    }
    return () => { if (loopIntervalRef.current) clearInterval(loopIntervalRef.current); };
  }, [lesson.videoId, isPlayerReady]);

  useEffect(() => {
    if (activeMedia?.type !== 'video') {
      if (playerRef.current && playerRef.current.pauseVideo) playerRef.current.pauseVideo();
      if (loopIntervalRef.current) clearInterval(loopIntervalRef.current);
      setActiveSegment(null);
    }
  }, [activeMedia]);

  const controlVideo = (action, segment) => {
    if (!playerRef.current) return;
    if (action === 'play') {
      onMediaPlay('video', 'yt-player');
      setActiveSegment(segment);
      playerRef.current.seekTo(segment.start);
      playerRef.current.playVideo();
      if (loopIntervalRef.current) clearInterval(loopIntervalRef.current);
      loopIntervalRef.current = setInterval(() => {
        if (playerRef.current.getCurrentTime() >= segment.end) playerRef.current.seekTo(segment.start);
      }, 500);
    } else if (action === 'pause') {
      playerRef.current.pauseVideo();
    } else if (action === 'stop') {
      setActiveSegment(null);
      if (loopIntervalRef.current) clearInterval(loopIntervalRef.current);
      playerRef.current.pauseVideo();
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-2 rounded-[2rem] shadow-md border-4 border-white relative aspect-video">
        <div className="bg-slate-900 rounded-[1.5rem] overflow-hidden w-full h-full relative z-10 shadow-inner">
          <div id="yt-player" className="w-full h-full"></div>
          {videoError && <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-50 z-30"><XCircle className="w-12 h-12 text-red-400 mb-4" /><p className="font-bold text-red-500">{videoError}</p></div>}
          {!isPlayerReady && !videoError && <div className="absolute inset-0 flex flex-col items-center justify-center bg-pink-50 z-20"><div className="animate-bounce mb-4"><Video className="w-12 h-12 text-pink-400" /></div><p className="font-bold text-pink-500">影片載入中...</p></div>}
        </div>
      </div>

      <div className="bg-white p-4 lg:p-8 rounded-[2rem] shadow-sm border-4 border-pink-50">
        <h3 className="text-xl font-extrabold mb-6 flex items-center gap-2 text-slate-700"><Sparkles className="w-6 h-6 text-yellow-400" /> 影片片段練習</h3>
        <div className="space-y-6">
          {(lesson.segments || []).map(seg => {
            const isActive = activeSegment?.id === seg.id;
            const segRecordings = recordings.filter(r => r.lessonDate === date && r.segmentId === seg.id);
            return (
              <div key={seg.id} className={`p-4 lg:p-6 rounded-3xl border-[3px] transition-all duration-300 ${isActive ? 'border-pink-300 bg-pink-50/50 shadow-sm scale-[1.01]' : 'border-slate-100 bg-white hover:border-pink-200'}`}>
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex-1">
                      <div className="inline-flex items-center gap-2 bg-white px-3 py-1 rounded-full border border-slate-200 text-xs font-bold text-slate-500 mb-2 shadow-sm">
                        <PlayCircle className="w-3 h-3 text-pink-500" /> {formatTime(seg.start)} - {formatTime(seg.end)}
                      </div>
                      
                      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 mt-3 items-center">
                         <span className="text-slate-400 text-base font-bold text-right whitespace-nowrap">目標句 :</span>
                         <span className="text-pink-600 bg-pink-100 px-3 py-1.5 rounded-xl text-lg font-extrabold leading-normal">{seg.targetText}</span>
                         
                         {seg.chineseText && (
                           <>
                             <span className="text-slate-400 text-base font-bold text-right whitespace-nowrap">中文意思 :</span>
                             <span className="text-slate-500 text-lg font-extrabold px-3 py-1.5 leading-normal">{seg.chineseText}</span>
                           </>
                         )}
                      </div>
                    </div>
                    <button onClick={() => isActive ? controlVideo('stop') : controlVideo('play', seg)} className={`shrink-0 flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-white shadow-md transition-all transform hover:scale-105 active:scale-95 ${isActive ? 'bg-slate-700 hover:bg-slate-800' : 'bg-pink-400 hover:bg-pink-500 shadow-pink-200'}`}>
                      {isActive ? <><StopCircle className="w-5 h-5" /> 停止</> : <><PlayCircle className="w-5 h-5" /> 開始</>}
                    </button>
                  </div>
                  
                  <div className="mt-2 pt-4 border-t-2 border-dashed border-pink-200">
                    <AudioRecorder 
                      segment={seg} 
                      lessonDate={date} 
                      currentUser={currentUser} 
                      settings={settings} 
                      onRecordStart={() => onMediaPlay('record', 'recording')} 
                    />
                  </div>

                  <div className="mt-4 space-y-4">
                    {segRecordings.length > 0 && <p className="text-xs font-bold text-slate-400 ml-1">大家的作品 ({segRecordings.length})</p>}
                    {segRecordings.map(rec => (
                      <RecordingItem 
                        key={rec.id} 
                        data={rec} 
                        currentUser={currentUser} 
                        activeMedia={activeMedia}
                        onMediaPlay={onMediaPlay}
                        users={users}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AudioRecorder({ segment, lessonDate, currentUser, settings, onRecordStart }) {
  const [isRecording, setIsRecording] = useState(false);
  const [timeLeft, setTimeLeft] = useState(settings?.maxRecordingSeconds || 60);
  const [confirmBlob, setConfirmBlob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isRecording) stopRecording(null); 
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isRecording]);

  const startRecording = async (e) => {
    if(e) { e.preventDefault(); e.stopPropagation(); }
    onRecordStart();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm';
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      mediaRecorderRef.current.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setConfirmBlob(blob); stream.getTracks().forEach(track => track.stop());
      };
      mediaRecorderRef.current.start();
      setIsRecording(true); setTimeLeft(settings?.maxRecordingSeconds || 60);
      timerRef.current = setInterval(() => setTimeLeft(prev => { if (prev <= 1) { stopRecording(); return 0; } return prev - 1; }), 1000);
    } catch (err) { alert("無法存取麥克風"); }
  };

  const stopRecording = (e) => {
    if(e) { e.preventDefault(); e.stopPropagation(); }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
    setIsRecording(false); if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleUpload = async () => {
    if (!confirmBlob) return;
    setUploading(true);
    const reader = new FileReader();
    reader.readAsDataURL(confirmBlob);
    reader.onloadend = async () => {
      try {
        await setDoc(doc(collection(db, getPath('recordings'))), {
          lessonDate, segmentId: segment.id, userId: currentUser.id, userName: currentUser.name, userRole: currentUser.role,
          userAvatar: currentUser.avatar || '',
          audioData: reader.result, duration: (settings?.maxRecordingSeconds || 60) - timeLeft, timestamp: new Date().toISOString(), comments: [], readBy: [], likes: []
        });
        setConfirmBlob(null);
      } catch (e) { alert("上傳失敗，檔案可能過大"); }
      setUploading(false);
    };
  };

  return (
    <div className="flex flex-col items-center">
      {!confirmBlob ? (
        <button 
          onMouseDown={startRecording} onMouseUp={stopRecording} onMouseLeave={stopRecording}
          onTouchStart={startRecording} onTouchEnd={stopRecording} onTouchCancel={stopRecording}
          onContextMenu={(e) => e.preventDefault()}
          className={`w-full py-4 rounded-2xl font-bold text-white transition-all shadow-md select-none touch-none flex items-center justify-center gap-2 ${isRecording ? 'bg-red-500 scale-95 animate-pulse' : 'bg-indigo-400 hover:bg-indigo-500 shadow-indigo-200 hover:-translate-y-0.5'}`}
        >
          {isRecording ? <Square /> : <Mic />} {isRecording ? `錄音中... 放開停止 (${timeLeft}s)` : '按住不放 錄製發音'}
        </button>
      ) : (
        <div className="w-full bg-slate-800 text-white p-4 rounded-2xl flex flex-col gap-3 animate-fade-in-down shadow-lg">
          <div className="flex justify-between items-center"><span className="font-bold text-sm">錄音完成！要上傳嗎？</span><button onClick={() => {const a=new Audio(URL.createObjectURL(confirmBlob)); a.play()}} className="text-xs bg-slate-600 hover:bg-slate-500 px-3 py-1.5 rounded-lg font-bold transition">試聽</button></div>
          <div className="flex gap-2">
            <button onClick={() => setConfirmBlob(null)} className="flex-1 py-2 bg-slate-600 hover:bg-slate-500 rounded-xl font-bold text-sm transition">取消重錄</button>
            <button onClick={handleUpload} disabled={uploading} className="flex-1 py-2 bg-green-500 hover:bg-green-600 rounded-xl font-bold text-sm flex items-center justify-center gap-1 transition">{uploading ? '上傳中...' : <><Upload size={16}/> 確認上傳</>}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RecordingItem({ data, currentUser, activeMedia, onMediaPlay, users }) {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const audioRef = useRef(null);
  const db = getFirestore();
  
  const isUnreviewed = !data.readBy || !data.readBy.includes('reviewed');
  const hasUnread = data.comments && data.comments.length > 0 && (!data.readBy || !data.readBy.includes(currentUser.id)) && !data.readBy?.includes('reviewed');
  const isPlaying = activeMedia?.type === 'audio' && activeMedia?.id === data.id;
  const isTeacherOrAdmin = ['admin', 'teacher'].includes(currentUser.role);
  const canDelete = currentUser.role === 'admin' || currentUser.id === data.userId;
  const isLiked = data.likes && data.likes.includes(currentUser.id);

  useEffect(() => {
    if (!isPlaying && audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
  }, [isPlaying]);

  // 新增：當這筆錄音從畫面上消失（例如切換日期）時，強制停止背景音檔
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  const togglePlay = () => {
    if (isPlaying) onMediaPlay(null, null);
    else {
      onMediaPlay('audio', data.id);
      if (!audioRef.current) audioRef.current = new Audio(data.audioData);
      audioRef.current.volume = 1.0;
      audioRef.current.play().catch(e => console.error("Play Error", e));
      audioRef.current.onended = () => onMediaPlay(null, null);
    }
  };

  const toggleComments = async () => {
    setShowComments(!showComments);
    if (!showComments && isUnreviewed && isTeacherOrAdmin) {
      await updateDoc(doc(db, getPath('recordings'), data.id), { readBy: arrayUnion('reviewed') });
    }
  };
  
  const toggleLike = async () => {
    const newLikes = isLiked ? arrayRemove(currentUser.id) : arrayUnion(currentUser.id);
    await updateDoc(doc(db, getPath('recordings'), data.id), { likes: newLikes });
  };
  
  const deleteComment = async (comment) => {
    if(!window.confirm("確定刪除留言？")) return;
    await updateDoc(doc(db, getPath('recordings'), data.id), { comments: arrayRemove(comment) });
  };

  const sendComment = async () => {
    if (!newComment.trim()) return;
    const newReadBy = isTeacherOrAdmin ? ['reviewed'] : [];
    await updateDoc(doc(db, getPath('recordings'), data.id), { 
      comments: arrayUnion({ 
        id: Date.now(), 
        userId: currentUser.id, 
        userName: currentUser.name, 
        userRole: currentUser.role, 
        userAvatar: currentUser.avatar || '', 
        text: newComment, 
        time: new Date().toISOString() 
      }),
      readBy: newReadBy 
    });
    setNewComment('');
  };

  const nameColor = data.userRole === 'admin' ? 'text-purple-600' : data.userRole === 'teacher' ? 'text-indigo-600' : 'text-orange-600';

  return (
    <div className={`bg-white border-2 rounded-[1.5rem] p-4 sm:p-5 shadow-sm transition-all ${isPlaying ? 'border-orange-300 ring-4 ring-orange-50' : 'border-slate-100 hover:border-pink-200 hover:shadow-md'}`}>
      
      {/* 頂部：頭像與資訊 */}
      <div className="flex gap-3 sm:gap-4 items-start mb-3">
        {/* 動態回查 users 資料表 (僅管理員有)，否則依賴當初上傳的備份 */}
        <UserAvatar avatar={data.userAvatar || users?.find(u=>u.id===data.userId)?.avatar} name={data.userName} className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 shadow-sm border border-slate-200" />
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`font-extrabold text-base sm:text-lg truncate max-w-[160px] sm:max-w-none ${nameColor}`}>{data.userName}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-extrabold border shadow-sm whitespace-nowrap ${data.userRole === 'admin' ? 'bg-purple-50 text-purple-600 border-purple-200' : data.userRole === 'teacher' ? 'bg-indigo-50 text-indigo-600 border-indigo-200' : 'bg-orange-50 text-orange-600 border-orange-200'}`}>
              {data.userRole === 'admin' ? '管理員' : data.userRole === 'teacher' ? '老師' : '學生'}
            </span>
          </div>
          <div className="text-[11px] text-slate-400 font-bold">
            {new Date(data.timestamp).toLocaleString([], {year: 'numeric', month:'numeric', day:'numeric', hour:'2-digit', minute:'2-digit'})}
          </div>
        </div>
      </div>

      {/* 底部：單行俐落的操作列 */}
      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
        {/* 左側：播放按鈕 */}
        <button onClick={togglePlay} className={`flex items-center justify-center gap-1.5 px-4 py-2 rounded-full font-bold text-sm transition-all shadow-sm ${isPlaying ? 'bg-orange-400 text-white shadow-orange-200' : 'bg-slate-50 text-slate-600 hover:bg-orange-50 hover:text-orange-500 border border-slate-100 hover:border-orange-200'}`}>
          {isPlaying ? <><Square size={14} fill="currentColor"/> 停止</> : <><Play size={14} fill="currentColor"/> 聆聽</>}
        </button>

        {/* 右側：社群互動按鈕群 */}
        <div className="flex items-center gap-1 sm:gap-2">
          <button onClick={toggleLike} className={`flex items-center gap-1 px-3 py-2 rounded-full font-bold text-sm transition-colors ${isLiked ? 'text-pink-500 bg-pink-50' : 'text-slate-400 hover:bg-slate-50 hover:text-pink-500'}`}>
            <ThumbsUp size={18} fill={isLiked ? "currentColor" : "none"} />
            {data.likes?.length > 0 && <span>{data.likes.length}</span>}
          </button>
          
          <button onClick={toggleComments} className={`flex items-center gap-1 px-3 py-2 rounded-full font-bold text-sm transition-colors relative ${showComments ? 'text-blue-500 bg-blue-50' : 'text-slate-400 hover:bg-slate-50 hover:text-blue-500'}`}>
             <div className="relative">
               <MessageCircle size={18} />
               {hasUnread && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white"></span>}
             </div>
             {data.comments?.length > 0 && <span>{data.comments.length}</span>}
          </button>

          {canDelete && (
            <button onClick={() => window.confirm("確定刪除？") && deleteDoc(doc(db, getPath('recordings'), data.id))} className="px-3 py-2 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors">
              <Trash2 size={18} />
            </button>
          )}
        </div>
      </div>

      {/* 留言區塊強化版 */}
      {showComments && (
        <div className="mt-3 pt-3 border-t border-slate-100 animate-fade-in-down">
          <div className="space-y-4 mb-4 max-h-56 overflow-y-auto pr-1 custom-scrollbar">
            {(data.comments || []).map(c => (
              <div key={c.id} className="bg-slate-50 p-3 sm:p-4 rounded-[1.5rem] text-sm border border-slate-100 shadow-sm relative group flex items-start gap-3 sm:gap-4">
                {/* 留言區放大頭像，並支援歷史無頭像資料補完 */}
                <UserAvatar avatar={c.userAvatar || users?.find(u=>u.id===c.userId)?.avatar} name={c.userName} className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 shadow-sm border border-slate-200" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap justify-between items-center mb-1 gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`font-extrabold text-sm sm:text-base ${c.userRole === 'admin' ? 'text-purple-600' : c.userRole === 'teacher' ? 'text-indigo-600' : 'text-orange-600'}`}>{c.userName}</span>
                      {/* 加上身分標籤 */}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-extrabold border shadow-sm whitespace-nowrap ${c.userRole === 'admin' ? 'bg-purple-50 text-purple-600 border-purple-200' : c.userRole === 'teacher' ? 'bg-indigo-50 text-indigo-600 border-indigo-200' : 'bg-orange-50 text-orange-600 border-orange-200'}`}>
                        {c.userRole === 'admin' ? '管理員' : c.userRole === 'teacher' ? '老師' : '學生'}
                      </span>
                    </div>
                    {/* 加入年、月、日的時間 */}
                    <span className="text-[10px] sm:text-[11px] text-slate-400 font-bold shrink-0">
                      {new Date(c.time).toLocaleString([], {year: 'numeric', month:'numeric', day:'numeric', hour:'2-digit', minute:'2-digit'})}
                    </span>
                  </div>
                  <p className="text-slate-700 text-sm font-bold leading-relaxed break-words mt-1">{c.text}</p>
                </div>
                <div className="w-6 shrink-0 flex justify-end">
                  {(currentUser.role === 'admin' || currentUser.id === c.userId) ? (
                    <button onClick={() => deleteComment(c)} className="text-slate-300 hover:text-red-400 p-1 opacity-60 hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                  ) : <div className="w-6 h-4"></div>}
                </div>
              </div>
            ))}
            {(!data.comments || data.comments.length === 0) && <p className="text-center text-xs text-slate-400 py-3 font-bold">還沒有留言，當第一個吧！ 💬</p>}
          </div>
          <div className="flex gap-2">
            <input type="text" value={newComment} onChange={e => setNewComment(e.target.value)} className="flex-1 p-3 text-sm border-2 border-slate-200 rounded-xl outline-none focus:border-blue-400 font-bold text-slate-600 bg-slate-50 focus:bg-white transition-colors" placeholder="給點鼓勵..." />
            <button onClick={sendComment} className="px-5 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl shadow-md transition-colors"><Send size={18} /></button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminDashboard({ users, lessons, settings }) {
  const [tab, setTab] = useState('lessons');
  return (
    <div className="bg-white rounded-[2rem] shadow-sm border-4 border-pink-50 overflow-hidden min-h-[600px]">
      <div className="flex bg-pink-50/50 p-2 gap-2 border-b-4 border-pink-50 overflow-x-auto">
        <button onClick={() => setTab('lessons')} className={`flex-1 py-3 px-6 font-extrabold rounded-2xl flex items-center justify-center gap-2 transition-all whitespace-nowrap ${tab === 'lessons' ? 'bg-white text-pink-500 shadow-sm border-2 border-pink-100' : 'text-slate-400 hover:bg-white/50'}`}><Video className="w-5 h-5" /> 排課</button>
        <button onClick={() => setTab('users')} className={`flex-1 py-3 px-6 font-extrabold rounded-2xl flex items-center justify-center gap-2 transition-all whitespace-nowrap ${tab === 'users' ? 'bg-white text-pink-500 shadow-sm border-2 border-pink-100' : 'text-slate-400 hover:bg-white/50'}`}><Users className="w-5 h-5" /> 會員</button>
        <button onClick={() => setTab('settings')} className={`flex-1 py-3 px-6 font-extrabold rounded-2xl flex items-center justify-center gap-2 transition-all whitespace-nowrap ${tab === 'settings' ? 'bg-white text-pink-500 shadow-sm border-2 border-pink-100' : 'text-slate-400 hover:bg-white/50'}`}><Settings className="w-5 h-5" /> 設定</button>
      </div>
      <div className="p-4 md:p-8">
        {tab === 'lessons' && <LessonManager lessons={lessons} />}
        {tab === 'users' && <UserManager users={users} />}
        {tab === 'settings' && <SettingsManager settings={settings} />}
      </div>
    </div>
  );
}

function PreviewVideoPlayer({ videoId, start, end }) {
  const playerRef = useRef(null);
  const loopIntervalRef = useRef(null);

  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
    const init = () => {
      if (playerRef.current) return;
      playerRef.current = new window.YT.Player('preview-yt-player', {
        videoId: videoId,
        playerVars: { autoplay: 1, controls: 1, rel: 0 },
        events: {
          onReady: (e) => {
            e.target.seekTo(start);
            e.target.playVideo();
            loopIntervalRef.current = setInterval(() => {
              if (e.target.getCurrentTime() >= end) {
                 e.target.seekTo(start); 
              }
            }, 500);
          }
        }
      });
    };
    if (window.YT && window.YT.Player) init();
    else window.onYouTubeIframeAPIReady = init;

    return () => { if (loopIntervalRef.current) clearInterval(loopIntervalRef.current); };
  }, [videoId, start, end]);

  return <div id="preview-yt-player" className="w-full h-full"></div>;
}

function LessonManager({ lessons }) {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [local, setLocal] = useState({ videoId: '', title: '', segments: [], isLocked: false });
  const [msg, setMsg] = useState('');
  
  const [previewVideo, setPreviewVideo] = useState(null);

  const getLocalToday = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => { 
    if (date === new Date().toISOString().split('T')[0]) {
      setDate(getLocalToday());
    }
  }, []);

  useEffect(() => { setLocal(lessons[date] || { videoId: '', title: '', segments: [], isLocked: false }); }, [date, lessons]);

  const save = async () => {
    await setDoc(doc(db, getPath('lessons'), date), local);
    setMsg('✅ 雲端儲存成功！'); setTimeout(() => setMsg(''), 2000);
  };

  const addSeg = () => setLocal({...local, segments: [...(local.segments||[]), {id: Date.now(), start:0, end:10, startBtnText:'▶️ 開始', stopBtnText:'⏹️ 停止', targetText:'', chineseText:''}]});
  const updateSeg = (id, f, v) => setLocal({...local, segments: local.segments.map(s => s.id === id ? {...s, [f]: v} : s)});
  const disabled = local.isLocked;

  return (
    <div className="space-y-8 relative">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="col-span-1 space-y-3">
          <label className="block text-sm font-extrabold text-slate-700">1. 選擇日期排課</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full h-12 p-3 border-2 border-slate-200 bg-slate-50 rounded-2xl outline-none focus:bg-white focus:ring-4 focus:ring-pink-100 focus:border-pink-400 font-bold transition-all cursor-pointer appearance-none" style={{ WebkitAppearance: 'none' }}/>
          <LessonDateIndicators lessons={lessons} onSelectDate={setDate} selectedDate={date} />
        </div>
        <div className="col-span-1 md:col-span-2 space-y-3 relative">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-extrabold text-slate-700">2. 課程資訊</label>
            <button onClick={() => setLocal({...local, isLocked: !local.isLocked})} className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold transition-colors ${local.isLocked ? 'bg-red-100 text-red-500' : 'bg-green-100 text-green-600'}`}>{local.isLocked ? <><Lock size={12}/> 已鎖定</> : <><Unlock size={12}/> 編輯中</>}</button>
          </div>
          <input type="text" disabled={disabled} value={local.title} onChange={e => setLocal({...local, title: e.target.value})} className={`w-full p-3 border-2 rounded-2xl outline-none font-bold transition-all ${disabled ? 'bg-slate-100 text-slate-400 border-slate-100' : 'border-slate-200 bg-slate-50 focus:bg-white focus:ring-4 focus:ring-pink-100 focus:border-pink-400'}`} placeholder="課程標題" />
          <div className="flex gap-2 items-center">
            <span className="text-slate-500 bg-slate-100 p-3 rounded-2xl border-2 border-slate-200 font-bold text-xs sm:text-sm hidden sm:block">youtube.com/watch?v=</span>
            <input type="text" disabled={disabled} value={local.videoId} onChange={e => setLocal({...local, videoId: e.target.value})} className={`flex-1 p-3 border-2 rounded-2xl outline-none font-bold font-mono transition-all ${disabled ? 'bg-slate-100 text-slate-400 border-slate-100' : 'border-slate-200 bg-slate-50 focus:bg-white focus:ring-4 focus:ring-pink-100 focus:border-pink-400'}`} placeholder="ID" />
          </div>
        </div>
      </div>

      <div className="border-t-4 border-slate-100 pt-8">
        <div className="flex justify-between items-center mb-6">
          <label className="block text-lg font-extrabold text-slate-700 flex items-center gap-2"><Video className="w-5 h-5 text-pink-400" /> 3. 練習片段</label>
          {!disabled && <button onClick={addSeg} className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-2xl font-bold hover:bg-slate-700 shadow-md text-sm"><Plus className="w-4 h-4" /> 新增</button>}
        </div>

        <div className="space-y-4">
          {(local.segments || []).map((seg, idx) => (
            <div key={seg.id} className={`bg-white p-4 sm:p-6 rounded-3xl border-[3px] relative group transition-colors ${disabled ? 'border-slate-100 opacity-80' : 'border-slate-100 hover:border-pink-200 shadow-sm'}`}>
              
              <div className="flex justify-between items-center mb-4">
                <div className="font-extrabold text-pink-500 flex items-center gap-2">
                  <span className="bg-pink-100 w-8 h-8 rounded-full flex items-center justify-center text-pink-600 text-sm">{idx + 1}</span>片段
                </div>
                {!disabled && (
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => {
                        if(!local.videoId) { alert("請先填寫上方課程資訊的 YouTube ID"); return; }
                        setPreviewVideo({ videoId: local.videoId, start: seg.start, end: seg.end });
                      }} 
                      className="flex items-center gap-1 px-3 py-1.5 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg text-xs font-bold transition-colors"
                    >
                      <PlayCircle size={14} /> 預覽確認
                    </button>
                    <button onClick={() => setLocal({...local, segments: local.segments.filter(s=>s.id!==seg.id)})} className="text-slate-400 hover:text-red-500 bg-slate-50 hover:bg-red-50 p-2 rounded-xl transition-all"><Trash2 className="w-4 h-4" /></button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 bg-slate-50 p-2 rounded-xl border border-slate-100"><label className="text-xs text-slate-500 font-bold sm:w-12 text-center">開始</label><input disabled={disabled} type="number" value={seg.start} onChange={e => updateSeg(seg.id, 'start', Number(e.target.value))} className="w-full p-2 border-2 border-slate-200 rounded-lg font-bold text-center outline-none focus:border-pink-400 text-sm disabled:bg-slate-100" /></div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2 bg-slate-50 p-2 rounded-xl border border-slate-100"><label className="text-xs text-slate-500 font-bold sm:w-12 text-center">結束</label><input disabled={disabled} type="number" value={seg.end} onChange={e => updateSeg(seg.id, 'end', Number(e.target.value))} className="w-full p-2 border-2 border-slate-200 rounded-lg font-bold text-center outline-none focus:border-pink-400 text-sm disabled:bg-slate-100" /></div>
              </div>
              <div className="space-y-2">
                <input disabled={disabled} type="text" value={seg.targetText} onChange={e => updateSeg(seg.id, 'targetText', e.target.value)} className="w-full p-3 border-2 border-slate-200 bg-slate-50 rounded-2xl outline-none focus:border-pink-400 font-bold text-sm disabled:bg-slate-100" placeholder="日文目標句" />
                <input disabled={disabled} type="text" value={seg.chineseText || ''} onChange={e => updateSeg(seg.id, 'chineseText', e.target.value)} className="w-full p-3 border-2 border-slate-200 bg-slate-50 rounded-2xl outline-none focus:border-orange-400 font-bold text-sm disabled:bg-slate-100" placeholder="中文意思 (選填)" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end pt-6 border-t-4 border-slate-100">
        <button onClick={save} className="bg-pink-400 hover:bg-pink-500 text-white font-extrabold text-lg py-3 px-8 rounded-full shadow-lg shadow-pink-200 transition-all hover:-translate-y-1">{msg || '儲存設定 ✨'}</button>
      </div>

      {previewVideo && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-[2rem] p-6 w-full max-w-2xl shadow-2xl relative animate-fade-in-down border-4 border-slate-50">
            <button onClick={() => setPreviewVideo(null)} className="absolute -top-4 -right-4 bg-white text-slate-400 hover:text-red-500 w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 border-slate-100 transition-transform hover:scale-110">
              <XCircle className="w-6 h-6" />
            </button>
            <h3 className="text-xl font-extrabold text-slate-700 mb-4 flex items-center gap-2">
              <PlayCircle className="text-indigo-500" /> 片段預覽 ({formatTime(previewVideo.start)} - {formatTime(previewVideo.end)})
            </h3>
            <div className="aspect-video rounded-xl overflow-hidden bg-slate-900 shadow-inner">
               <PreviewVideoPlayer videoId={previewVideo.videoId} start={previewVideo.start} end={previewVideo.end} />
            </div>
            <p className="text-xs text-slate-400 text-center mt-4 font-bold">預覽功能會自動從設定的「開始」秒數播放到「結束」秒數，並自動重播。</p>
          </div>
        </div>
      )}
    </div>
  );
}

// v4.20 完全極簡化後台，專注於 Email 與最新資安設定
function UserManager({ users }) {
  const [nu, setNu] = useState({email:'', name:'', role:'student', status:'active', avatar:''});
  const [mod, setMod] = useState({type:null, data:null});
  const fileInputRef = useRef(null); 

  const add = async (e) => { 
    e.preventDefault(); 
    if (!nu.email) {
      setMod({type:'alert', data:'⚠️ Email 是必填欄位！'});
      return;
    }
    
    setMod({type:'alert', data:'⏳ 正在為使用者建立安全連線...'});

    try {
      // 💡 雙實例技術：使用 secondaryAuth 建立帳號，避免管理員被登出
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, nu.email, "123456");
      
      const id = `u_${Date.now()}`; 
      await setDoc(doc(db, getPath('users'), id), {
        id: id,
        name: nu.name,
        email: nu.email,
        role: nu.role,
        status: nu.status,
        avatar: nu.avatar,
        authUid: userCredential.user.uid // 儲存真實的加密 UID，不存密碼！
      }); 
      
      // 完成後登出 secondaryAuth 清理背景連線
      await signOut(secondaryAuth);
      
      setNu({email:'', name:'', role:'student', status:'active', avatar:''}); 
      setMod({type:'alert', data:'✅ 新會員已成功建立！預設密碼為 123456'}); 
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setMod({type:'alert', data:'❌ 建立失敗：此 Email 已被其他帳號註冊過囉！'});
      } else {
        setMod({type:'alert', data:'❌ 建立失敗：' + err.message});
      }
    }
  };

  const update = async () => { 
    try {
      setMod({type:'alert', data:'⏳ 更新中...'});
      let authUidToSave = mod.data.authUid;

      // 支援管理員填補舊會員 Email 順便註冊的功能
      if (mod.data.email && !authUidToSave) {
        try {
          const userCredential = await createUserWithEmailAndPassword(secondaryAuth, mod.data.email, "123456");
          authUidToSave = userCredential.user.uid;
          await signOut(secondaryAuth);
        } catch (authErr) {
           if (authErr.code !== 'auth/email-already-in-use') {
              throw authErr; // 若不是重複註冊錯誤，就拋出
           }
        }
      }

      await updateDoc(doc(db, getPath('users'), mod.data.id), { 
        name: mod.data.name, 
        email: mod.data.email || '', 
        role: mod.data.role, 
        avatar: mod.data.avatar,
        ...(authUidToSave && { authUid: authUidToSave }) // 只有當 authUid 存在時才寫入
      }); 
      setMod({type:'alert', data:'✅ 會員資料更新完成！'}); 
    } catch (e) {
      setMod({type:'alert', data:'❌ 更新失敗：' + e.message}); 
    }
  };
  
  const handleImageUpload = (e, isEditMode = false) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const MAX_SIZE = 150;
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > MAX_SIZE) { height *= MAX_SIZE / width; width = MAX_SIZE; }
        } else {
          if (height > MAX_SIZE) { width *= MAX_SIZE / height; height = MAX_SIZE; }
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        const base64 = canvas.toDataURL('image/jpeg', 0.8);
        if (isEditMode) {
            setMod({ ...mod, data: { ...mod.data, avatar: base64 } });
        } else {
            setNu({ ...nu, avatar: base64 });
        }
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-8 relative">
      {/* 新增會員區塊 */}
      <div className="bg-slate-50 p-5 rounded-[2rem] border-[3px] border-slate-100">
        <h3 className="font-extrabold text-lg mb-4 text-slate-700">新增會員 (預設密碼：123456)</h3>
        {/* v4.20 移除舊帳號密碼輸入，僅保留必備欄位，上傳頭像改為直覺按鈕 */}
        <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
           <input value={nu.name} onChange={e=>setNu({...nu,name:e.target.value})} placeholder="姓名" className="p-3 border-2 rounded-xl font-bold outline-none focus:border-indigo-400 text-sm" required />
           <input type="email" value={nu.email} onChange={e=>setNu({...nu,email:e.target.value})} placeholder="登入用 Email (必填)" className="p-3 border-2 rounded-xl font-bold outline-none focus:border-indigo-400 text-sm" required />
           <select value={nu.role} onChange={e=>setNu({...nu,role:e.target.value})} className="p-3 border-2 rounded-xl font-bold outline-none focus:border-indigo-400 bg-white text-sm"><option value="student">學生</option><option value="teacher">老師</option><option value="admin">管理員</option></select>
           
           <label className="w-full p-3 border-2 border-slate-200 rounded-xl font-bold bg-white text-left text-sm flex items-center justify-between cursor-pointer hover:bg-slate-50 transition focus-within:border-indigo-400">
             {nu.avatar ? <span className="truncate text-indigo-500">已選圖片</span> : <span className="text-slate-400">上傳頭像 (選填)</span>}
             <ImageIcon size={16} className="text-slate-400"/>
             <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, false)} />
           </label>
           
           <button className="bg-indigo-500 text-white py-3 rounded-xl font-bold shadow-md hover:bg-indigo-600 transition-all text-sm w-full">建立帳號</button>
        </form>
      </div>

      <div className="overflow-x-auto border-[3px] border-slate-100 rounded-[2rem] shadow-sm"><div className="min-w-[600px]"><table className="w-full text-left border-collapse text-sm"><thead><tr className="bg-slate-50 text-slate-500 font-extrabold">
          <th className="p-4 border-b-2 border-slate-100">頭像</th>
          <th className="p-4 border-b-2 border-slate-100">姓名</th>
          <th className="p-4 border-b-2 border-slate-100">Email</th>
          <th className="p-4 border-b-2 border-slate-100">角色</th>
          <th className="p-4 border-b-2 border-slate-100">狀態</th>
          <th className="p-4 border-b-2 border-slate-100">操作</th>
        </tr></thead><tbody>{users.map(u => (<tr key={u.id} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors ${u.status === 'suspended' ? 'opacity-50' : ''}`}>
          <td className="p-4"><UserAvatar avatar={u.avatar} name={u.name} className="w-8 h-8 border border-slate-200" /></td>
          <td className="p-4 font-bold text-slate-700">{u.name}</td>
          {/* v4.20 修復 Email 上下置中與拔除 Icon */}
          <td className="p-4 font-bold font-mono text-slate-500">{u.email ? u.email : <span className="text-slate-300 text-xs">未綁定</span>}</td>
          <td className="p-4"><span className={`px-3 py-1.5 rounded-full text-xs font-extrabold border shadow-sm whitespace-nowrap ${u.role === 'admin' ? 'bg-purple-50 text-purple-600 border-purple-200' : u.role === 'teacher' ? 'bg-indigo-50 text-indigo-600 border-indigo-200' : 'bg-orange-50 text-orange-600 border-orange-200'}`}>{u.role === 'admin' ? '管理員' : u.role === 'teacher' ? '老師' : '學生'}</span></td>
          <td className="p-4"><span className={`px-3 py-1.5 rounded-full text-xs font-extrabold border shadow-sm whitespace-nowrap ${u.status === 'suspended' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-600 border-green-200'}`}>{u.status === 'suspended' ? '已停權' : '正常'}</span></td>
          <td className="p-4 flex items-center gap-2">
            <button title="編輯資料" className="bg-white p-2 rounded-xl border-2 border-slate-100 text-slate-400 hover:text-indigo-500 transition-all shadow-sm" onClick={() => setMod({ type: 'edit', data: { ...u } })}><Edit2 className="w-4 h-4" /></button>
            {/* v4.20 已移除修改密碼的鑰匙按鈕 */}
            <button title="切換停權" className={`bg-white p-2 rounded-xl border-2 border-slate-100 transition-all shadow-sm ${u.status === 'suspended' ? 'text-red-400 hover:text-green-500' : 'text-slate-400 hover:text-orange-500'}`} onClick={() => setMod({ type: 'confirm', data: { message: `確定要${u.status==='active'?'停權':'復權'} ${u.name} 嗎？`, onConfirm: async () => { if(u.role!=='admin') await updateDoc(doc(db, getPath('users'), u.id), { status: u.status === 'active' ? 'suspended' : 'active' }); else alert('不可停權管理員'); setMod({type:null}); } } })}>{u.status === 'suspended' ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}</button>
            <button title="刪除帳號" className="bg-white p-2 rounded-xl border-2 border-slate-100 text-slate-400 hover:text-red-500 transition-all shadow-sm" onClick={() => setMod({ type: 'confirm', data: { message: `確定要刪除 ${u.name} 嗎？此操作無法復原。`, onConfirm: async () => { if(u.role!=='admin') await deleteDoc(doc(db, getPath('users'), u.id)); else alert('不可刪除管理員'); setMod({type:null}); } } })}><Trash2 className="w-4 h-4" /></button>
          </td>
        </tr>))}</tbody></table></div></div>

      {mod.type === 'edit' && (
        <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full shadow-2xl border-4 border-slate-50 animate-fade-in-down">
            <h3 className="font-extrabold text-xl mb-6 text-slate-700 flex items-center gap-2"><div className="bg-indigo-100 p-2 rounded-full"><Edit2 className="w-5 h-5 text-indigo-500"/></div> 編輯會員</h3>
            <div className="space-y-4 mb-8">
              <div><label className="block text-sm font-bold text-slate-600 mb-2 ml-1">姓名</label><input type="text" className="w-full p-4 border-2 border-slate-200 focus:border-indigo-400 rounded-2xl outline-none font-bold" value={mod.data.name} onChange={e => setMod({ ...mod, data: { ...mod.data, name: e.target.value } })} /></div>
              <div><label className="block text-sm font-bold text-slate-600 mb-2 ml-1">Email</label><input type="email" className="w-full p-4 border-2 border-slate-200 focus:border-indigo-400 rounded-2xl outline-none font-bold" value={mod.data.email || ''} onChange={e => setMod({ ...mod, data: { ...mod.data, email: e.target.value } })} placeholder="尚未綁定 Email" /></div>
              <div><label className="block text-sm font-bold text-slate-600 mb-2 ml-1">角色</label><select value={mod.data.role} onChange={e => setMod({ ...mod, data: { ...mod.data, role: e.target.value } })} className="w-full p-4 border-2 border-slate-200 focus:border-indigo-400 rounded-2xl outline-none font-bold bg-white" disabled={mod.data.role === 'admin'}><option value="student">學生</option><option value="teacher">老師</option><option value="admin">管理員</option></select></div>
              <div>
                <label className="block text-sm font-bold text-slate-600 mb-2 ml-1">頭像</label>
                <div className="flex gap-2 items-center">
                  <div className="flex-1 p-2 border-2 rounded-xl flex items-center gap-2">
                     <UserAvatar avatar={mod.data.avatar} name={mod.data.name} className="w-8 h-8 border border-slate-200" />
                     <span className="text-xs text-slate-400 truncate">{mod.data.avatar ? '自訂圖片' : '預設頭像'}</span>
                  </div>
                  <label className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-4 py-3 rounded-xl text-sm font-bold cursor-pointer transition">
                    上傳圖片
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, true)} />
                  </label>
                   {mod.data.avatar && (
                    <button 
                      type="button"
                      onClick={() => setMod({ ...mod, data: { ...mod.data, avatar: '' } })}
                      className="bg-red-100 hover:bg-red-200 text-red-500 px-4 py-3 rounded-xl text-sm font-bold transition"
                    >
                      移除
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3"><button className="px-6 py-3 text-slate-500 hover:bg-slate-100 rounded-full font-bold" onClick={() => setMod({ type: null })}>取消</button><button className="px-6 py-3 bg-indigo-500 text-white rounded-full font-bold" onClick={update}>儲存</button></div>
          </div>
        </div>
      )}
      
      {mod.type === 'confirm' && (<div className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm flex items-center justify-center z-50 p-4"><div className="bg-white rounded-[2rem] p-8 max-w-sm w-full shadow-2xl border-4 border-slate-50 animate-fade-in-down text-center"><h3 className="font-extrabold text-xl mb-4 text-slate-700">系統確認</h3><p className="mb-8 text-slate-500 font-bold leading-relaxed">{mod.data.message}</p><div className="flex justify-center gap-3"><button className="flex-1 py-3 text-slate-500 hover:bg-slate-100 rounded-full font-bold transition-colors" onClick={() => setMod({ type: null })}>取消</button><button className="flex-1 py-3 bg-pink-500 text-white rounded-full font-bold hover:bg-pink-600 transition shadow-md shadow-pink-200" onClick={mod.data.onConfirm}>確定執行</button></div></div></div>)}
      {mod.type === 'alert' && (<div className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm flex items-center justify-center z-50 p-4"><div className="bg-white rounded-[2rem] p-8 max-w-sm w-full shadow-2xl border-4 border-slate-50 animate-fade-in-down text-center"><CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" /><p className="mb-8 text-slate-500 font-bold">{mod.data}</p><button className="w-full py-4 bg-slate-800 text-white rounded-full font-bold" onClick={() => setMod({ type: null })}>OK</button></div></div>)}
    </div>
  );
}

function SettingsManager({ settings }) {
  const [ls, setLs] = useState(settings || {maxRecordingSeconds:60, retentionMonths:6});
  const [msg, setMsg] = useState('');
  const save = async () => { await setDoc(doc(db, getPath('settings'), 'global'), ls); setMsg('✅ 更新成功！'); setTimeout(()=>setMsg(''),2000); };
  return (
    <div className="max-w-xl space-y-6">
      <div className="bg-slate-50 p-6 rounded-[2rem] border-[3px] border-slate-100">
        <h3 className="font-extrabold text-xl mb-8 flex items-center gap-2 text-slate-700"><div className="bg-orange-100 text-orange-500 p-2 rounded-xl"><Settings className="w-5 h-5"/></div> 錄音規範</h3>
        <div className="space-y-8">
          <div className="flex justify-between items-center bg-slate-50 p-5 rounded-2xl border border-slate-100"><label className="font-extrabold text-slate-700">單次錄音時限</label><div className="flex items-center gap-2 bg-white p-2 rounded-xl border-2 border-slate-200"><input type="number" value={ls.maxRecordingSeconds} onChange={e => setLs({...ls, maxRecordingSeconds: Number(e.target.value)})} className="w-16 p-2 text-center font-extrabold outline-none focus:text-orange-500" /><span className="text-slate-400 font-bold pr-2">秒</span></div></div>
          <div className="flex justify-between items-center bg-slate-50 p-5 rounded-2xl border border-slate-100"><label className="font-extrabold text-slate-700">錄音檔保存期限</label><div className="flex items-center gap-2 bg-white p-2 rounded-xl border-2 border-slate-200"><input type="number" value={ls.retentionMonths} onChange={e => setLs({...ls, retentionMonths: Number(e.target.value)})} className="w-16 p-2 text-center font-extrabold outline-none focus:text-orange-500" /><span className="text-slate-400 font-bold pr-2">個月</span></div></div>
        </div>
        <div className="mt-10 pt-6 border-t-[3px] border-slate-100 flex items-center gap-4"><button onClick={save} className="w-full bg-slate-800 hover:bg-slate-700 text-white font-extrabold py-3 px-8 rounded-full shadow-lg hover:scale-[1.02] transition-transform">{msg || '同步設定 ☁️'}</button></div>
      </div>
    </div>
  );
}

function formatTime(s) { return `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}`; }